const NOTIFY_ME_ENDPOINT = 'https://api.care.ugaoo.com/api/v1/stock-notifications';
const NOTIFY_ME_TIMEOUT_MS = 15000;
const NOTIFY_ME_STORAGE_KEY = 'ugaoo:notify-me:contact';
const NOTIFY_ME_PHONE_MAX_DIGITS = 12;
const NOTIFY_ME_SUBSCRIPTIONS_KEY = 'ugaoo:notify-me:subscriptions';
// The backend itself only keeps a subscription "live" for 90 days, and a
// product can go back in stock and out again in the meantime (which makes
// the customer eligible to resubscribe). Expiring the local flag well
// before that keeps a stale "already subscribed" badge from permanently
// hiding the form for a product that needs a fresh subscription.
const NOTIFY_ME_SUBSCRIPTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

if (!customElements.get('ugaoo-notify-me')) {
  customElements.define('ugaoo-notify-me', class UgaooNotifyMe extends HTMLElement {
    static get observedAttributes() {
      return ['data-product-id'];
    }

    constructor() {
      super();

      this.trigger = this.querySelector('.ugaoo-notify-me__trigger');
      this.directSubmitButton = this.querySelector('[data-notify-submit]');
      this.modal = this.querySelector('modal-dialog:not([data-notify-status-modal])');
      this.statusModal = this.querySelector('[data-notify-status-modal]');
      this.form = this.querySelector('[data-notify-form]');
      this.subtitle = this.querySelector('[data-notify-subtitle]');
      this.titleEl = this.querySelector('[data-notify-title]');
      this.nameInput = this.querySelector('[data-notify-name-input]');
      this.phoneInput = this.querySelector('[data-notify-phone-input]');
      this.fieldsSubmit = this.querySelector('[data-notify-fields-submit]');
      this.errorEl = this.querySelector('[data-notify-error]');
      this.modalStatusEl = this.querySelector('[data-notify-modal-status]');
      this.modalStatusText = this.querySelector('[data-notify-modal-status-text]');
      this.modalStatusClose = this.querySelector('[data-notify-modal-status-close]');

      this.isSubmitting = false;
      this.abortController = null;
      this.requestId = 0;

      this.restoreSavedContact();
      this.applySubscribedState();

      if (this.nameInput) {
        this.nameInput.addEventListener('input', () => this.saveContact());
      }
      if (this.phoneInput) {
        this.phoneInput.addEventListener('input', () => this.safely(() => {
          const digitsOnly = this.phoneInput.value.replace(/\D/g, '').slice(0, NOTIFY_ME_PHONE_MAX_DIGITS);
          if (digitsOnly !== this.phoneInput.value) this.phoneInput.value = digitsOnly;
          this.saveContact();
        }));
      }

      if (this.modalStatusClose) {
        this.modalStatusClose.addEventListener('click', () => this.safely(() => {
          (this.modal || this.statusModal)?.hide();
        }));
      }

      if (this.directSubmitButton) {
        this.directSubmitButton.addEventListener('click', () => this.safely(() => {
          const productId = (this.dataset.productId || '').trim();
          if (this.isProductSubscribed(productId)) {
            this.showStatus(this.dataset.alreadySubscribedText, 'success');
            return;
          }
          this.submit({ name: this.dataset.customerName, phone: this.dataset.customerPhone });
        }));
      }
      if (this.fieldsSubmit) {
        this.fieldsSubmit.addEventListener('click', () => this.safely(this.onFieldsSubmitClick.bind(this)));
      }
    }

    // Shared (page-wide) instance only: point it at the product of the card
    // button that was clicked, then behave like that product's own trigger.
    openFor(triggerEl) {
      const productId = (triggerEl.dataset.productId || '').trim();
      if (!productId) return;
      // One-click (no popup) subscribe still in flight: switching product would
      // reset() and silently abort it, so ignore clicks until it settles.
      if (!this.modal && this.isSubmitting) return;

      const title = triggerEl.dataset.productTitle || '';
      if (!this._successTemplate) this._successTemplate = this.dataset.successText || '';
      // Function replacer: a "$" in the title must not be read as a replace pattern.
      this.dataset.successText = this._successTemplate.replace('__TITLE__', () => title);

      // "ugaoo-coming-soon" products: title the popup "Coming Soon" instead of "Notify Me".
      const popupTitle = triggerEl.hasAttribute('data-coming-soon')
        ? this.dataset.comingSoonTitle
        : this.dataset.defaultTitle;
      if (popupTitle) {
        if (this.titleEl) this.titleEl.textContent = popupTitle;
        this.querySelectorAll('[role="dialog"]').forEach((dialog) => dialog.setAttribute('aria-label', popupTitle));
      }

      // attributeChangedCallback resets form/status state when the product changes.
      this.setAttribute('data-product-id', productId);
      this.trigger = triggerEl;

      if (this.modal) {
        this.modal.show(triggerEl);
        return;
      }
      // Logged in with a phone on file: submit straight away, like the PDP button.
      if (this.isProductSubscribed(productId)) {
        this.showStatus(this.dataset.alreadySubscribedText, 'success');
        return;
      }
      this.submit({ name: this.dataset.customerName, phone: this.dataset.customerPhone });
    }

    // Catches anything unexpected in a UI callback (a missing dependency, a
    // custom element that hasn't upgraded yet, etc.) so one bad interaction
    // can't leave the control permanently stuck disabled/mid-submit.
    safely(fn) {
      try {
        fn();
      } catch (error) {
        this.setSubmitting(false);
        console.error('[ugaoo-notify-me]', error);
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'data-product-id' && oldValue !== null && oldValue !== newValue) {
        this.reset();
      }
    }

    // Restores the control to its initial state so a stale success/error
    // popup from a previous product can never bleed into a new one if this
    // element is ever reused (e.g. patched in place) rather than replaced.
    reset() {
      this.cancelPendingRequest();
      this.setSubmitting(false);
      this.safely(() => this.modal?.hide());
      this.safely(() => this.statusModal?.hide());
      if (this.trigger) this.trigger.hidden = false;
      if (this.form) this.form.hidden = false;
      if (this.subtitle) this.subtitle.hidden = false;
      if (this.modalStatusEl) this.modalStatusEl.hidden = true;
      if (this.errorEl) this.errorEl.hidden = true;
      if (this.nameInput) this.nameInput.value = '';
      if (this.phoneInput) this.phoneInput.value = '';
      this.restoreSavedContact();
      this.applySubscribedState();
    }

    // The trigger button always stays clickable and keeps its normal label
    // (it sits next to the quantity selector, so relabeling/disabling it
    // inline looked cramped). Instead, this pre-populates what the popup
    // shows the moment it opens: the already-subscribed message if this
    // product was subscribed to recently, or the normal form otherwise.
    applySubscribedState() {
      const productId = (this.dataset.productId || '').trim();
      const subscribed = this.isProductSubscribed(productId);

      if (this.form) this.form.hidden = subscribed;
      if (this.subtitle) this.subtitle.hidden = subscribed;
      if (this.modalStatusEl) this.modalStatusEl.hidden = !subscribed;

      if (subscribed && this.modalStatusEl && this.modalStatusText) {
        this.modalStatusEl.classList.remove('ugaoo-notify-me-modal__status--error');
        this.modalStatusText.textContent = this.dataset.alreadySubscribedText || '';
      }
    }

    getSubscriptions() {
      try {
        const raw = window.localStorage?.getItem(NOTIFY_ME_SUBSCRIPTIONS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch {
        return {};
      }
    }

    isProductSubscribed(productId) {
      if (!productId) return false;
      const subscribedAt = this.getSubscriptions()[productId];
      return typeof subscribedAt === 'number' && Date.now() - subscribedAt < NOTIFY_ME_SUBSCRIPTION_TTL_MS;
    }

    markProductSubscribed(productId) {
      if (!productId) return;
      try {
        const subscriptions = this.getSubscriptions();
        subscriptions[productId] = Date.now();
        window.localStorage?.setItem(NOTIFY_ME_SUBSCRIPTIONS_KEY, JSON.stringify(subscriptions));
      } catch {
        // Storage full/unavailable — the confirmation still showed this time,
        // it just won't be remembered on the next visit.
      }
    }

    // Remembers what the customer typed (name/phone) across page reloads
    // and other product pages, since a modal is easy to abandon (e.g. to
    // check the label on their previous order) and re-typing is friction.
    restoreSavedContact() {
      const saved = this.readSavedContact();
      if (!saved) return;
      if (this.nameInput && !this.nameInput.value && typeof saved.name === 'string') {
        this.nameInput.value = saved.name;
      }
      if (this.phoneInput && !this.phoneInput.value && typeof saved.phone === 'string') {
        this.phoneInput.value = saved.phone;
      }
    }

    readSavedContact() {
      try {
        const raw = window.localStorage?.getItem(NOTIFY_ME_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        // Storage unavailable (private browsing, disabled) or corrupt value — ignore.
        return null;
      }
    }

    saveContact() {
      try {
        window.localStorage?.setItem(NOTIFY_ME_STORAGE_KEY, JSON.stringify({
          name: this.nameInput?.value || '',
          phone: this.phoneInput?.value || '',
        }));
      } catch {
        // Storage full/unavailable — non-critical, the form still works in-session.
      }
    }

    cancelPendingRequest() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    onFieldsSubmitClick() {
      const name = (this.nameInput?.value || '').trim();
      const phone = (this.phoneInput?.value || '').trim();

      if (this.nameInput && !name) {
        this.showError(this.errorEl?.dataset.nameInvalidText);
        return;
      }
      if (!this.isValidPhone(phone)) {
        this.showError(this.errorEl?.dataset.phoneInvalidText);
        return;
      }

      if (this.errorEl) this.errorEl.hidden = true;
      this.saveContact();
      this.submit({ name: name || this.dataset.customerName, phone });
    }

    // Mirrors the backend's accepted formats (bare 10-digit, +91-prefixed,
    // 0-prefixed) so obviously invalid numbers are caught client-side
    // instead of costing a round trip to the API.
    isValidPhone(phone) {
      const digits = (phone || '').replace(/\D/g, '');
      if (digits.length === 10) return true;
      if (digits.length === 12 && digits.startsWith('91')) return true;
      if (digits.length === 11 && digits.startsWith('0')) return true;
      return false;
    }

    showError(text) {
      if (!this.errorEl) return;
      this.errorEl.textContent = text || this.dataset.errorText || '';
      this.errorEl.hidden = false;
    }

    submit({ name, phone }) {
      if (this.isSubmitting) return;

      const productId = (this.dataset.productId || '').trim();
      const trimmedName = (name || '').trim();
      const trimmedPhone = (phone || '').trim();

      if (!productId || !trimmedPhone) {
        // Nothing sensible to send (missing product/phone) — fail fast
        // without a network call rather than surfacing a raw API error.
        this.reportFailure(this.dataset.errorText);
        return;
      }

      const payload = { name: trimmedName, phone: trimmedPhone, product_id: productId };

      this.cancelPendingRequest();
      this.setSubmitting(true);
      const requestId = ++this.requestId;

      this.submitNotifyRequest(payload)
        .then(() => {
          this.markProductSubscribed(productId);
          if (requestId !== this.requestId) return;
          this.showStatus(this.dataset.successText, 'success');
        })
        .catch((error) => {
          if (requestId !== this.requestId) return;
          if (error?.name === 'AbortError' && !error.isTimeout) {
            // Superseded by a newer request/reset — the newer flow owns the UI now.
            return;
          }
          this.reportFailure(error?.message || this.dataset.errorText);
        })
        .finally(() => {
          if (requestId !== this.requestId) return;
          this.setSubmitting(false);
        });
    }

    // Routes an error either into the inline field-error (while the input
    // modal is still open) or the shared status view, whichever applies.
    reportFailure(message) {
      if (this.modal && this.modalStatusEl && this.form && !this.form.hidden) {
        this.showError(message);
        return;
      }
      this.showStatus(message, 'error');
    }

    openStatusModal(modal) {
      if (!modal || modal.hasAttribute('open')) return;
      if (typeof modal.show !== 'function') return;
      modal.show(this.trigger);
    }

    async submitNotifyRequest(payload) {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        this.abortController?.abort();
      }, NOTIFY_ME_TIMEOUT_MS);

      let response;
      try {
        response = await fetch(NOTIFY_ME_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal,
        });
      } catch (error) {
        if (error?.name === 'AbortError') {
          if (timedOut) {
            const timeoutError = new Error(this.dataset.timeoutText || this.dataset.errorText);
            timeoutError.name = 'AbortError';
            timeoutError.isTimeout = true;
            throw timeoutError;
          }
          throw error;
        }
        throw new Error(this.dataset.errorText);
      } finally {
        clearTimeout(timeoutId);
        this.abortController = null;
      }

      if (response.ok) return;

      if (response.status === 429) {
        throw new Error(this.dataset.rateLimitedText || this.dataset.errorText);
      }

      if (response.status === 422) {
        const body = await response.json().catch(() => null);
        if (typeof body?.detail === 'string' && body.detail) {
          throw new Error(body.detail);
        }
      }

      throw new Error(this.dataset.errorText);
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      if (this.directSubmitButton) this.directSubmitButton.disabled = isSubmitting;
      if (this.fieldsSubmit) this.fieldsSubmit.disabled = isSubmitting;
      // Shared popup, one-click path: the card button is the only visible control.
      if (this.hasAttribute('data-shared') && !this.modal && this.trigger) {
        this.trigger.classList.toggle('loading', isSubmitting);
        this.trigger.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      }
    }

    showStatus(text, state) {
      const modal = this.modal || this.statusModal;
      if (!modal || !this.modalStatusEl) {
        // No status surface available to render into — fall back to an
        // alert so the customer still learns the outcome instead of the
        // button silently re-enabling with no feedback.
        if (text) window.alert(text);
        return;
      }

      if (this.form) this.form.hidden = true;
      if (this.subtitle) this.subtitle.hidden = true;
      this.safely(() => this.openStatusModal(modal));
      this.modalStatusEl.hidden = false;
      this.modalStatusEl.classList.toggle('ugaoo-notify-me-modal__status--error', state === 'error');
      if (this.modalStatusText) this.modalStatusText.textContent = text || '';
    }
  });

  // Product-card "Notify Me" buttons (snippets/product-card.liquid) carry only
  // the product id/title; the one shared popup (theme.liquid) does the rest.
  // Delegated so it also covers cards rendered later by AJAX filtering.
  document.addEventListener('click', (event) => {
    const triggerEl = event.target.closest('[data-notify-card-trigger]');
    if (!triggerEl) return;
    event.preventDefault();
    const shared = document.querySelector('ugaoo-notify-me[data-shared]');
    if (!shared || typeof shared.openFor !== 'function') return;
    shared.safely(() => shared.openFor(triggerEl));
  });
}
